import { Card, Checkbox, Collapse, Flex, Space } from 'antd'
import React, { useState } from 'react'
import { useParams } from 'react-router'
import { FilterParamNames } from '../../../../../../constants/assignment-filter-params'
import { FLAGGED_FOR_MYSELF_PARAM, FLAGGED_PARAM, MISMATCH_PARAM, TEST_SET_ONLY_PARAM } from '../../../../../../constants/query-params'
import useQueryParams from '../../../../../../hooks/useQueryParams'
import useQueryParamSetter from '../../../../../../hooks/useQueryParamSetter'
import useQueryParam from '../../../../../../hooks/useQueryParam'
import ConfidenceFilters from './ConfidenceFilters'
import ReviewFilters from './ReviewStatusFilters'
import ResetFiltersButton from './ResetFiltersButton'
import assignmentSelectors from '../../../../../../store/selectors/assignment.selectors'
import { useSelector } from 'react-redux'
import AssigneeFilters from './AssigneeFilters'
import { useFetchAssignmentSharedWithQuery } from '../../../../../../store/apis/shared-assignment.api'
import { fetchAssignmentSharedWithSelectors } from '../../../../../../store/apis/utils/selectors/shared-assignment.api.selectors'
import { isMba, isNewTextAssignment } from '../../../../../../utils/assignment.helper'
import { useVersionId } from '../../../../../../utils/parseURL.helper'
import { useFetchQuestionsQuery } from '../../../../../../store/apis/question.api'
import { fetchQuestionsSelectors } from '../../../../../../store/apis/utils/selectors/question.api.selectors'
import useResetFilters from './useResetFilters'

const FiltersCard = () => {
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const assignmentType = useSelector(assignmentSelectors.type)
    const language = useSelector(assignmentSelectors.language)

    const { token, layerId: questionId } = useParams()
    const versionId = useVersionId()
    const setQueryParam = useQueryParamSetter()
    const params = useQueryParams(FilterParamNames)
    const mismatch = params[0]
    const escalated = params[1]
    const escalatedForMyself = params[4]

    const { data: users } = useFetchAssignmentSharedWithQuery(
        { token },
        {
            selectFromResult: result => fetchAssignmentSharedWithSelectors.selectOnlyUsers(result),
        },
    )
    
    const { data: question } = useFetchQuestionsQuery(
        { token, versionId },
        {
            skip: !isNewTextAssignment(language) || ['all', 'dashboard'].includes(questionId),
            selectFromResult: result => fetchQuestionsSelectors.byId({ id: Number(questionId) }, result),
        },
    )


    const [activePanels, setActivePanels] = useState(['review-status', 'confidence', 'assignee', 'properties'])
    const testSetOnlyParam = useQueryParam(TEST_SET_ONLY_PARAM) === 'true'
    useResetFilters(questionId, testSetOnlyParam)

    const handleCollapseChange = newActivePanels => setActivePanels(newActivePanels)

    const handleMismatchChange = event => setQueryParam({ [MISMATCH_PARAM]: event.target.checked ? 'true' : undefined })

    const handleEscalatedChange = event => setQueryParam({ [FLAGGED_PARAM]: event.target.checked ? 'true' : undefined })

    const handleEscalatedForMyselfChange = event =>
        setQueryParam({ [FLAGGED_FOR_MYSELF_PARAM]: event.target.checked ? 'true' : undefined })

    const collapsePanels = [
        {
            key: 'review-status',
            label: 'Review Status',
            children: <ReviewFilters />,
        },
        ...(questionId !== 'all' && (isMba(assignmentType) || testSetOnlyParam)
            ? [
                  {
                      key: 'confidence',
                      label: 'Confidence',
                      children: <ConfidenceFilters />,
                  },
              ]
            : []),
        ...(users?.length && questionId !== 'all' && isOwner
            ? [
                  {
                      key: 'assignee',
                      label: 'Assignee',
                      children: <AssigneeFilters />,
                  },
              ]
            : []),
        {
            key: 'properties',
            label: 'Properties',
            children: (
                <Space direction='vertical'>
                    {questionId !== 'all' && (isMba(assignmentType) || testSetOnlyParam || !question?.lastTrained) && (
                        <Checkbox checked={mismatch} onChange={handleMismatchChange}>
                            Mismatch
                        </Checkbox>
                    )}
                    <Checkbox checked={escalated} onChange={handleEscalatedChange}>
                        Escalated
                    </Checkbox>
                    <Checkbox checked={escalatedForMyself} onChange={handleEscalatedForMyselfChange}>
                        Escalated for myself
                    </Checkbox>
                </Space>
            ),
        },
    ]

    return (
        <Card
            title={
                <Flex style={{ width: '100%', padding: '8px 0' }} justify='space-between' align='center'>
                    <div>Filters</div>
                    <ResetFiltersButton />
                </Flex>
            }
            size='small'
        >
            <Collapse
                ghost
                bordered={false}
                onChange={handleCollapseChange}
                activeKey={activePanels}
                items={collapsePanels}
            />
        </Card>
    )
}

export default FiltersCard
